import React, { useCallback, useEffect, useRef } from 'react';
import { Application } from 'pixi.js';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import { CommentDrawingRef, Layer, Comment } from '../../types/typing.ts';
import { renderCommentHighlights } from './utils/renderCommentHighlight.ts';
import { isBackgroundRecordingApp } from './utils/backgroundRecordingState';

// Drawings are only shown while the playhead is at (or very near) the comment's
// timestamp, so the pencil sketch lines up with its timeline position.
const COMMENT_TIME_TOLERANCE = 0.1;

interface DrawingRendererProps {
  app: Application | null;
  commentContainerRef: React.MutableRefObject<Map<string, CommentDrawingRef>>;
  isCommentsOpen: boolean;
  comments: Comment[];
  width: number;
  height: number;
  zIndex: number;
  layers: Layer[] | undefined;
  timelineState?: React.MutableRefObject<TimelineState | null>;
}

const DrawingRenderer: React.FC<DrawingRendererProps> = ({
  app,
  commentContainerRef,
  isCommentsOpen,
  comments,
  width,
  height,
  zIndex,
  layers,
  timelineState,
}) => {
  const isCommentsOpenRef = useRef<boolean>(false);
  const currentTimeRef = useRef<number>(timelineState?.current?.getTime?.() ?? 0);
  const commentsRef = useRef<Comment[]>(comments);
  commentsRef.current = comments;

  useEffect(() => {
    isCommentsOpenRef.current = isCommentsOpen;
  }, [isCommentsOpen]);

  // Toggle each rendered drawing's visibility based on whether the playhead is
  // at that comment's stored timestamp and the comments panel is open.
  const syncCommentVisibility = useCallback(() => {
    commentContainerRef.current.forEach((commentDrawing, id) => {
      const comment = commentsRef.current.find((c) => c.id === id);
      const nearTimestamp = comment
        ? Math.abs(currentTimeRef.current - (comment.timestamp ?? 0)) <=
          COMMENT_TIME_TOLERANCE
        : false;
      commentDrawing.pathContainer.visible =
        isCommentsOpenRef.current && nearTimestamp;
    });
  }, [commentContainerRef]);

  useEffect(() => {
    const drawCommentHighlightsOnCanvas = async () => {
      if (app) {
        comments.forEach((comment) => {
          if (
            !commentContainerRef.current.has(comment.id) &&
            comment.pathPoints &&
            comment.pathPoints.length > 0
          ) {
            renderCommentHighlights(
              app,
              comment,
              zIndex,
              isCommentsOpenRef,
              commentContainerRef
            );
          }
        });

        syncCommentVisibility();
      }
    };
    drawCommentHighlightsOnCanvas();
    return () => {
      if (isBackgroundRecordingApp(app)) {
        return;
      }

      commentContainerRef.current.forEach((commentContainer) => {
        commentContainer.destroy();
      });
    };
  }, [comments, width, height]);

  // Listen to the timeline playhead so drawings only appear while the current
  // time matches their stored timestamp.
  useEffect(() => {
    let attachedEngine: TimelineState | null = null;
    let timeHandler: ((args: { time: number }) => void) | null = null;
    let attachInterval: number | undefined;

    const attachControls = (engine: TimelineState | null) => {
      if (!engine || attachedEngine === engine) return;
      attachedEngine = engine;

      timeHandler = ({ time }: { time: number }) => {
        currentTimeRef.current = time;
        syncCommentVisibility();
      };
      engine.listener.on('afterSetTime', timeHandler);
      engine.listener.on('setTimeByTick', timeHandler);

      currentTimeRef.current = engine.getTime?.() ?? 0;
      syncCommentVisibility();
    };

    attachControls(timelineState?.current || null);
    // The engine may not be ready when this renderer mounts, so poll briefly.
    if (!attachedEngine) {
      attachInterval = window.setInterval(() => {
        attachControls(timelineState?.current || null);
        if (attachedEngine && attachInterval) {
          window.clearInterval(attachInterval);
          attachInterval = undefined;
        }
      }, 300);
    }

    return () => {
      if (attachInterval) window.clearInterval(attachInterval);
      if (attachedEngine && timeHandler) {
        attachedEngine.listener.off('afterSetTime', timeHandler);
        attachedEngine.listener.off('setTimeByTick', timeHandler);
      }
    };
  }, [timelineState, syncCommentVisibility, app]);

  useEffect(() => {
    if (layers && layers.length > 0) {
      commentContainerRef.current?.forEach((charRef) => {
        charRef.pathContainer.zIndex = layers.length;
        app?.stage.sortChildren();
      });
    } else {
      commentContainerRef.current?.forEach((charRef) => {
        charRef.pathContainer.zIndex = 0;
        app?.stage.sortChildren();
      });
    }
  }, [layers]);

  return null;
};

export default DrawingRenderer;
